import { isToday } from 'date-fns';
import { RefObject, useCallback, useEffect } from 'react';
import { CashInfoByCurrency, SourceCurrencyList } from '../../types';
import { isDefined, LoadableState } from '../../utils';
import { CurrencyExchangeSourceAndTargetCurrencyManager } from './CurrencyExchangeSourceAndTargetCurrencyManager';

export type UseSourceAndTargetCurrenciesCommonParams = {
  currencyExchangeEvents: RefObject<CurrencyExchangeSourceAndTargetCurrencyManager>;
  shouldLoadSourceCurrencies: boolean;
  cashInfoByCurrency: LoadableState<CashInfoByCurrency>;
  showError: (msg: string) => void;
  haveNightPeriods: Array<string>;
};

export type UseSourceAndTargetCurrenciesCommonOutput = {
  loadSourceCurrencies: CurrencyExchangeSourceAndTargetCurrencyManager['loadSourceCurrencies'];
  loadTargetCurrencies: CurrencyExchangeSourceAndTargetCurrencyManager['loadTargetCurrencies'];
  startRefreshCurrencies: CurrencyExchangeSourceAndTargetCurrencyManager['startRefreshCurrencies'];
  stopRefreshCurrencies: CurrencyExchangeSourceAndTargetCurrencyManager['stopRefreshCurrencies'];
  isNightPeriodActive: (list: LoadableState<SourceCurrencyList>) => boolean;
  resetLoadables: () => void;
};

export const useSourceAndTargetCurrenciesCommon = ({
  currencyExchangeEvents,
  shouldLoadSourceCurrencies,
  cashInfoByCurrency,
  showError,
  haveNightPeriods,
}: UseSourceAndTargetCurrenciesCommonParams): UseSourceAndTargetCurrenciesCommonOutput => {
  const loadSourceCurrencies = useCallback<UseSourceAndTargetCurrenciesCommonOutput['loadSourceCurrencies']>(
    async props => currencyExchangeEvents.current.loadSourceCurrencies(props),
    [currencyExchangeEvents],
  );

  const loadTargetCurrencies = useCallback<UseSourceAndTargetCurrenciesCommonOutput['loadTargetCurrencies']>(
    async currency => currencyExchangeEvents.current.loadTargetCurrencies(currency),
    [currencyExchangeEvents],
  );

  const startRefreshCurrencies = useCallback<UseSourceAndTargetCurrenciesCommonOutput['startRefreshCurrencies']>(
    () => currencyExchangeEvents.current.startRefreshCurrencies(),
    [currencyExchangeEvents],
  );

  const stopRefreshCurrencies = useCallback<UseSourceAndTargetCurrenciesCommonOutput['stopRefreshCurrencies']>(
    () => currencyExchangeEvents.current.stopRefreshCurrencies(),
    [currencyExchangeEvents],
  );

  const resetLoadables = useCallback(() => {
    currencyExchangeEvents.current.resetLoadables();
  }, [currencyExchangeEvents]);

  /**
   * Returns true if we are in the night period. This is needed in various cases when we are trying to determine why a currency is not available.
   */
  const isNightPeriodActive = useCallback(
    (list: LoadableState<SourceCurrencyList>): boolean => {
      return (
        list.isLoaded &&
        list
          .filter(_ => haveNightPeriods.includes(_.currency))
          .some(
            _ =>
              isDefined(_.exchangePeriodFrom) &&
              isDefined(_.exchangePeriodTo) &&
              isToday(new Date(_.exchangePeriodFrom)) &&
              isToday(new Date(_.exchangePeriodTo)) &&
              _.exchangePeriodFrom.endsWith('T17:00:00') &&
              _.exchangePeriodTo.endsWith('T22:00:00'),
          )
      );
    },
    [haveNightPeriods],
  );

  useEffect(() => {
    currencyExchangeEvents.current.shouldLoadSourceCurrencies = shouldLoadSourceCurrencies;
  }, [shouldLoadSourceCurrencies, currencyExchangeEvents]);

  useEffect(() => {
    currencyExchangeEvents.current.isCashInfoLoaded = cashInfoByCurrency.isLoaded;
  }, [cashInfoByCurrency.isLoaded, currencyExchangeEvents]);

  useEffect(() => {
    currencyExchangeEvents.current.showError = showError;
  }, [showError, currencyExchangeEvents]);

  return {
    loadSourceCurrencies,
    loadTargetCurrencies,
    startRefreshCurrencies,
    stopRefreshCurrencies,
    isNightPeriodActive,
    resetLoadables,
  };
};
